import { useState, type ReactNode } from 'react';

import { Box, Card, Collapse, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

// The block a Loft document is made of. Lifted out of HandoverPage so the plan
// page is the same document rather than a second one that looks nearly the same:
// both are Floral White surfaces on the Gardenia page, titled with an overline.

export const SectionTitle = ({
  label,
  isNew = false,
}: {
  label: string;
  /** In red: new since the last look. See FlowStep.isNew. */
  isNew?: boolean;
}) => (
  <Typography
    sx={{ color: isNew ? 'error.main' : 'text.secondary' }}
    variant='overline'
  >
    {label}
  </Typography>
);

// Each section gets its own Floral White surface on the Gardenia page, so the
// document scans as distinct blocks.
const Panel = ({
  label,
  fill = false,
  hug = false,
  quiet = false,
  collapsible = false,
  isNew = false,
  background,
  action,
  children,
}: {
  label: string;
  /** A small control beside the title, e.g. a play button to present it. */
  action?: ReactNode;
  /** Its title in red: a section new since the last look. */
  isNew?: boolean;
  // Fill the parent flex cell (side-by-side panels match heights).
  fill?: boolean;
  /**
   * As wide as what is in it rather than the page: a flow of four screens gets
   * a panel four screens wide, not one stretched to the longest flow.
   */
  hug?: boolean;
  /**
   * Held back rather than being built. The page colour instead of the surface,
   * so the panel reads as sitting behind the work rather than as part of it.
   */
  quiet?: boolean;
  /**
   * Shut until somebody opens it. For a section that is on the page to be
   * findable rather than to be read: a parked journey is a dozen screens of
   * something nobody is building, and scrolling past them every time is a cost
   * paid by everyone to serve the one person who came looking.
   */
  collapsible?: boolean;
  /** A fill of its own in place of the surface, to set a group of sections apart. */
  background?: string;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(!collapsible);

  return (
    <Card
      sx={{
        p: 3,
        ...(fill && { flex: 1, width: '100%' }),
        ...(hug && { alignSelf: 'flex-start', maxWidth: '100%' }),
        ...(quiet && { bgcolor: 'custom.neutral.100' }),
        ...(background && { bgcolor: background }),
      }}
    >
      <Stack spacing={2}>
        {collapsible ? (
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            direction='row'
            spacing={1}
            onClick={() => setOpen((o) => !o)}
          >
            <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
              <SectionTitle label={label} isNew={isNew} />
              {action}
            </Stack>
            <Box sx={{ color: 'text.secondary', display: 'flex' }}>
              <Icon
                icon={(open ? 'chevron-up' : 'chevron-down') as never}
                fontSize={12}
              />
            </Box>
          </Stack>
        ) : (
          <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
            <SectionTitle label={label} isNew={isNew} />
            {action}
          </Stack>
        )}
        {/* Unmounted while shut, not hidden. Every mini in here is a live
            prototype, and a section nobody has opened should cost nothing. */}
        <Collapse in={open} unmountOnExit>
          {children}
        </Collapse>
      </Stack>
    </Card>
  );
};

export default Panel;
